(function () {
  'use strict';

  var MAX_RESULTS = 50;
  var CACHE_VERSION = 'v4';

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getSearchTerms(query) {
    return normalizeText(query)
      .split(' ')
      .filter(function (term) {
        return term.length >= 2;
      });
  }

  function getKeywordText(keywords) {
    return Array.isArray(keywords)
      ? keywords.join(' ')
      : '';
  }

  function scoreArticle(article, query, terms) {
    var normalizedQuery = normalizeText(query);
    var title = normalizeText(article.title);
    var excerpt = normalizeText(article.excerpt);
    var category = normalizeText(article.category);
    var keywords = normalizeText(
      getKeywordText(article.keywords)
    );

    var score = 0;

    if (!normalizedQuery) {
      return 0;
    }

    if (title === normalizedQuery) {
      score += 100;
    }

    if (title.indexOf(normalizedQuery) === 0) {
      score += 35;
    } else if (
      title.indexOf(normalizedQuery) !== -1
    ) {
      score += 25;
    }

    if (
      keywords.indexOf(normalizedQuery) !== -1
    ) {
      score += 20;
    }

    if (
      category.indexOf(normalizedQuery) !== -1
    ) {
      score += 12;
    }

    if (
      excerpt.indexOf(normalizedQuery) !== -1
    ) {
      score += 8;
    }

    terms.forEach(function (term) {
      if (title.indexOf(term) !== -1) {
        score += 10;
      }

      if (keywords.indexOf(term) !== -1) {
        score += 7;
      }

      if (category.indexOf(term) !== -1) {
        score += 4;
      }

      if (excerpt.indexOf(term) !== -1) {
        score += 2;
      }
    });

    return score;
  }

  function searchArticles(articles, query) {
    var terms = getSearchTerms(query);

    if (!terms.length) {
      return [];
    }

    return articles
      .map(function (article) {
        return {
          article: article,
          score: scoreArticle(
            article,
            query,
            terms
          )
        };
      })
      .filter(function (item) {
        return item.score > 0;
      })
      .sort(function (a, b) {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        return String(
          a.article.title
        ).localeCompare(
          String(b.article.title)
        );
      })
      .slice(0, MAX_RESULTS)
      .map(function (item) {
        return item.article;
      });
  }

  function getPreviewThemeId() {
    return new URL(
      window.location.href
    ).searchParams.get(
      'preview_theme_id'
    );
  }

  function buildEndpointUrl(
    root,
    pageNumber
  ) {
    var rawUrl = root.getAttribute(
      'data-index-url'
    );

    var endpointUrl = new URL(
      rawUrl,
      window.location.origin
    );

    var previewThemeId =
      getPreviewThemeId();

    endpointUrl.searchParams.set(
      'view',
      'help-center-index'
    );

    endpointUrl.searchParams.set(
      'page',
      String(pageNumber)
    );

    if (previewThemeId) {
      endpointUrl.searchParams.set(
        'preview_theme_id',
        previewThemeId
      );
    }

    return endpointUrl.toString();
  }

  function buildHelpCenterDestinationUrl(
    url
  ) {
    var destination = new URL(
      url,
      window.location.origin
    );

    var previewThemeId =
      getPreviewThemeId();

    if (previewThemeId) {
      destination.searchParams.set(
        'preview_theme_id',
        previewThemeId
      );
    }

    return destination.toString();
  }

  function getCacheKey(root) {
    var previewThemeId =
      getPreviewThemeId() || 'live';

    var indexUrl =
      root.getAttribute(
        'data-index-url'
      ) || 'help-center';

    return [
      'hellomaterials-help-center-index',
      CACHE_VERSION,
      previewThemeId,
      indexUrl
    ].join(':');
  }

  function readCache(root) {
    try {
      var storedValue =
        window.sessionStorage.getItem(
          getCacheKey(root)
        );

      if (!storedValue) {
        return null;
      }

      var parsedValue =
        JSON.parse(storedValue);

      return Array.isArray(parsedValue)
        ? parsedValue
        : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(
    root,
    articles
  ) {
    try {
      window.sessionStorage.setItem(
        getCacheKey(root),
        JSON.stringify(articles)
      );
    } catch (error) {
      return;
    }
  }

  function clearCache(root) {
    try {
      window.sessionStorage.removeItem(
        getCacheKey(root)
      );
    } catch (error) {
      return;
    }
  }

  function fetchIndexPage(
    root,
    pageNumber
  ) {
    return fetch(
      buildEndpointUrl(
        root,
        pageNumber
      ),
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        }
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            'Help Center index request failed.'
          );
        }

        return response.text();
      })
      .then(function (responseText) {
        try {
          return JSON.parse(
            responseText
          );
        } catch (error) {
          throw new Error(
            'Help Center index returned invalid JSON.'
          );
        }
      });
  }

  function fetchAllArticles(root) {
    var cachedArticles =
      readCache(root);

    if (cachedArticles) {
      return Promise.resolve(
        cachedArticles
      );
    }

    return fetchIndexPage(
      root,
      1
    ).then(function (firstPage) {
      var firstArticles =
        Array.isArray(
          firstPage.articles
        )
          ? firstPage.articles
          : [];

      var totalPages =
        Number(firstPage.pages) || 1;

      if (totalPages <= 1) {
        writeCache(
          root,
          firstArticles
        );

        return firstArticles;
      }

      var pageRequests = [];

      for (
        var pageNumber = 2;
        pageNumber <= totalPages;
        pageNumber += 1
      ) {
        pageRequests.push(
          fetchIndexPage(
            root,
            pageNumber
          )
        );
      }

      return Promise.all(
        pageRequests
      ).then(
        function (remainingPages) {
          var allArticles =
            firstArticles.slice();

          remainingPages.forEach(
            function (pageData) {
              if (
                Array.isArray(
                  pageData.articles
                )
              ) {
                allArticles =
                  allArticles.concat(
                    pageData.articles
                  );
              }
            }
          );

          writeCache(
            root,
            allArticles
          );

          return allArticles;
        }
      );
    });
  }

  function createSvgIcon(type) {
    var namespace =
      'http://www.w3.org/2000/svg';

    var svg =
      document.createElementNS(
        namespace,
        'svg'
      );

    svg.setAttribute(
      'viewBox',
      '0 0 24 24'
    );

    svg.setAttribute(
      'fill',
      'none'
    );

    svg.setAttribute(
      'stroke',
      'currentColor'
    );

    svg.setAttribute(
      'stroke-width',
      '1.8'
    );

    svg.setAttribute(
      'stroke-linecap',
      'round'
    );

    svg.setAttribute(
      'stroke-linejoin',
      'round'
    );

    svg.setAttribute(
      'aria-hidden',
      'true'
    );

    svg.setAttribute(
      'focusable',
      'false'
    );

    if (type === 'chevron') {
      var chevronPath =
        document.createElementNS(
          namespace,
          'path'
        );

      chevronPath.setAttribute(
        'd',
        'm6 9 6 6 6-6'
      );

      svg.appendChild(
        chevronPath
      );

      return svg;
    }

    [
      'M6 3h8l4 4v14H6z',
      'M14 3v5h5',
      'M9 13h6',
      'M9 17h6'
    ].forEach(
      function (pathValue) {
        var path =
          document.createElementNS(
            namespace,
            'path'
          );

        path.setAttribute(
          'd',
          pathValue
        );

        svg.appendChild(path);
      }
    );

    return svg;
  }

  function pushDataLayerEvent(
    eventData
  ) {
    window.dataLayer =
      window.dataLayer || [];

    window.dataLayer.push(
      eventData
    );
  }

  function findAccordionAnswerContainer(
    accordionElement,
    preferredSelectors
  ) {
    if (!accordionElement) {
      return null;
    }

    var selectors =
      Array.isArray(preferredSelectors)
        ? preferredSelectors
        : [];

    for (
      var selectorIndex = 0;
      selectorIndex < selectors.length;
      selectorIndex += 1
    ) {
      var matchedElement =
        accordionElement.querySelector(
          selectors[selectorIndex]
        );

      if (matchedElement) {
        return matchedElement;
      }
    }

    var directChildren =
      Array.prototype.slice.call(
        accordionElement.children || []
      );

    for (
      var childIndex = 0;
      childIndex < directChildren.length;
      childIndex += 1
    ) {
      if (
        String(
          directChildren[childIndex].tagName ||
            ''
        ).toLowerCase() !==
        'summary'
      ) {
        return directChildren[childIndex];
      }
    }

    return null;
  }

  function createAnswerFeedback(
    metadata
  ) {
    metadata = metadata || {};

    var section =
      document.createElement(
        'section'
      );

    section.className =
      'help-center-helpful help-center-answer-feedback';

    section.setAttribute(
      'data-help-center-answer-feedback',
      ''
    );

    section.setAttribute(
      'aria-label',
      'Was this answer helpful?'
    );

    var title =
      document.createElement(
        'p'
      );

    title.className =
      'help-center-helpful__title';

    title.textContent =
      'Was this answer helpful?';

    section.appendChild(
      title
    );

    var buttonGroup =
      document.createElement(
        'div'
      );

    buttonGroup.className =
      'help-center-helpful__buttons';

    function createFeedbackButton(
      value,
      label,
      pathData
    ) {
      var button =
        document.createElement(
          'button'
        );

      button.className =
        'help-center-helpful__button';

      button.type =
        'button';

      button.setAttribute(
        'aria-pressed',
        'false'
      );

      button.setAttribute(
        'data-help-center-feedback',
        value
      );

      var svg =
        document.createElementNS(
          'http://www.w3.org/2000/svg',
          'svg'
        );

      svg.setAttribute(
        'viewBox',
        '0 0 24 24'
      );

      svg.setAttribute(
        'aria-hidden',
        'true'
      );

      svg.setAttribute(
        'focusable',
        'false'
      );

      var path =
        document.createElementNS(
          'http://www.w3.org/2000/svg',
          'path'
        );

      path.setAttribute(
        'd',
        pathData
      );

      svg.appendChild(
        path
      );

      var text =
        document.createElement(
          'span'
        );

      text.textContent =
        label;

      button.appendChild(
        svg
      );

      button.appendChild(
        text
      );

      return button;
    }

    var yesButton =
      createFeedbackButton(
        'yes',
        'Yes, it was',
        'M7 10v10H3V10h4Zm4 10H8V9l4-6c.8.2 1.4.9 1.4 1.8L13 8h5.5c1.5 0 2.6 1.4 2.2 2.8l-2 7A3 3 0 0 1 15.8 20H11Z'
      );

    var noButton =
      createFeedbackButton(
        'no',
        'No, I need more help',
        'M7 14V4H3v10h4Zm4-10H8v11l4 6c.8-.2 1.4-.9 1.4-1.8L13 16h5.5c1.5 0 2.6-1.4 2.2-2.8l-2-7A3 3 0 0 0 15.8 4H11Z'
      );

    buttonGroup.appendChild(
      yesButton
    );

    buttonGroup.appendChild(
      noButton
    );

    section.appendChild(
      buttonGroup
    );

    var message =
      document.createElement(
        'p'
      );

    message.className =
      'help-center-helpful__message';

    message.setAttribute(
      'data-help-center-feedback-message',
      ''
    );

    message.setAttribute(
      'aria-live',
      'polite'
    );

    message.setAttribute(
      'aria-atomic',
      'true'
    );

    section.appendChild(
      message
    );

    var support =
      document.createElement(
        'div'
      );

    support.className =
      'help-center-answer-feedback__support';

    support.hidden = true;

    support.setAttribute(
      'data-help-center-feedback-support',
      ''
    );

    var supportText =
      document.createElement(
        'p'
      );

    supportText.className =
      'help-center-answer-feedback__support-text';

    supportText.textContent =
      "Contact our support team and we'll help you directly.";

    var supportLink =
      document.createElement(
        'a'
      );

    supportLink.className =
      'help-center-answer-feedback__support-button';

    supportLink.href =
      buildHelpCenterDestinationUrl(
        '/pages/contact'
      );

    supportLink.textContent =
      'Contact Us';

    supportLink.setAttribute(
      'data-help-center-action',
      'contact_support'
    );

    supportLink.setAttribute(
      'data-help-center-context',
      'feedback_no'
    );

    support.appendChild(
      supportText
    );

    support.appendChild(
      supportLink
    );

    section.appendChild(
      support
    );

    var feedbackButtons = [
      yesButton,
      noButton
    ];

    function resetFeedbackButtons() {
      feedbackButtons.forEach(
        function (
          button
        ) {
          button.classList.remove(
            'help-center-helpful__button--selected'
          );

          button.setAttribute(
            'aria-pressed',
            'false'
          );
        }
      );
    }

    function getEventMetadata() {
      return {
        article_title:
          metadata.articleTitle || '',
        article_handle:
          metadata.articleHandle || '',
        article_url:
          metadata.articleUrl || '',
        category_name:
          metadata.categoryName || '',
        category_tag:
          metadata.categoryTag || '',
        feedback_context:
          metadata.context || ''
      };
    }

    feedbackButtons.forEach(
      function (
        button
      ) {
        button.addEventListener(
          'click',
          function () {
            var feedbackValue =
              button.getAttribute(
                'data-help-center-feedback'
              );

            resetFeedbackButtons();

            button.classList.add(
              'help-center-helpful__button--selected'
            );

            button.setAttribute(
              'aria-pressed',
              'true'
            );

            if (
              feedbackValue ===
              'yes'
            ) {
              support.hidden = true;

              message.textContent =
                'Thank you for your feedback.';
            } else {
              support.hidden = false;

              message.textContent =
                "Sorry this answer didn't help.";
            }

            var eventData =
              getEventMetadata();

            eventData.event =
              'help_center_article_feedback';

            eventData.feedback =
              feedbackValue;

            pushDataLayerEvent(
              eventData
            );
          }
        );
      }
    );

    supportLink.addEventListener(
      'click',
      function () {
        pushDataLayerEvent({
          event:
            'help_center_action_click',
          action_key:
            'contact_support',
          action_label:
            'Contact Us',
          context:
            'feedback_no',
          article_title:
            metadata.articleTitle || '',
          article_handle:
            metadata.articleHandle || '',
          article_url:
            metadata.articleUrl || '',
          category_name:
            metadata.categoryName || '',
          category_tag:
            metadata.categoryTag || ''
        });
      }
    );

    return section;
  }

  function createResultAccordion(
    root,
    article
  ) {
    var details =
      document.createElement(
        'details'
      );

    var summary =
      document.createElement(
        'summary'
      );

    var icon =
      document.createElement(
        'span'
      );

    var content =
      document.createElement(
        'span'
      );

    var category =
      document.createElement(
        'span'
      );

    var title =
      document.createElement(
        'span'
      );

    var arrow =
      document.createElement(
        'span'
      );

    var answer =
      document.createElement(
        'div'
      );

    var answerContent =
      document.createElement(
        'div'
      );

    details.className =
      'help-center-search-result';

    details.setAttribute(
      'data-help-center-search-answer',
      ''
    );

    details.setAttribute(
      'data-help-center-search-answer-title',
      article.title || ''
    );

    details.setAttribute(
      'data-help-center-search-answer-handle',
      article.handle || ''
    );

    summary.className =
      'help-center-search-result__summary';

    icon.className =
      'help-center-search-result__icon';

    icon.appendChild(
      createSvgIcon(
        'document'
      )
    );

    content.className =
      'help-center-search-result__content';

    category.className =
      'help-center-search-result__category';

    category.textContent =
      article.category ||
      'Help Center';

    title.className =
      'help-center-search-result__title';

    title.textContent =
      article.title ||
      'Help answer';

    arrow.className =
      'help-center-search-result__arrow';

    arrow.appendChild(
      createSvgIcon(
        'chevron'
      )
    );

    content.appendChild(
      category
    );

    content.appendChild(
      title
    );

    summary.appendChild(
      icon
    );

    summary.appendChild(
      content
    );

    summary.appendChild(
      arrow
    );

    answer.className =
      'help-center-search-result__answer';

    answerContent.className =
      'help-center-search-result__answer-content rte';

    if (article.content) {
      answerContent.innerHTML =
        article.content;
    } else if (article.excerpt) {
      var fallbackParagraph =
        document.createElement(
          'p'
        );

      fallbackParagraph.textContent =
        article.excerpt;

      answerContent.appendChild(
        fallbackParagraph
      );
    }

    answer.appendChild(
      answerContent
    );

    if (
      article.action &&
      article.action.label &&
      article.action.link
    ) {
      var action =
        document.createElement(
          'div'
        );

      var actionLink =
        document.createElement(
          'a'
        );

      action.className =
        'help-center-search-result__action';

      actionLink.className =
        'help-center-search-result__action-button';

      actionLink.href =
        buildHelpCenterDestinationUrl(
          article.action.link
        );

      actionLink.textContent =
        article.action.label;

      actionLink.setAttribute(
        'data-help-center-action',
        article.action.analyticsKey || ''
      );

      actionLink.setAttribute(
        'data-help-center-context',
        'search_answer'
      );

      actionLink.addEventListener(
        'click',
        function () {
          pushDataLayerEvent({
            event:
              'help_center_action_click',
            action_key:
              article.action.analyticsKey || '',
            action_label:
              article.action.label || '',
            context:
              'search_answer',
            article_title:
              article.title || ''
          });
        }
      );

      action.appendChild(
        actionLink
      );

      answer.appendChild(
        action
      );
    }

    answer.appendChild(
      createAnswerFeedback({
        articleTitle:
          article.title || '',
        articleHandle:
          article.handle || '',
        articleUrl:
          article.url || '',
        categoryName:
          article.category || '',
        categoryTag:
          article.categoryTag || '',
        context:
          'search_accordion'
      })
    );

    details.addEventListener(
      'toggle',
      function () {
        if (!details.open) {
          return;
        }

        root
          .querySelectorAll(
            '[data-help-center-search-answer][open]'
          )
          .forEach(
            function (
              otherDetails
            ) {
              if (
                otherDetails !==
                details
              ) {
                otherDetails.open =
                  false;
              }
            }
          );

        pushDataLayerEvent({
          event:
            'help_center_search_answer_open',
          article_title:
            article.title || '',
          article_handle:
            article.handle || '',
          category_name:
            article.category || '',
          category_tag:
            article.categoryTag || ''
        });
      }
    );

    details.appendChild(
      summary
    );

    details.appendChild(
      answer
    );

    return details;
  }

  function updateSearchUrl(query) {
    var updatedUrl = new URL(
      window.location.href
    );

    if (query) {
      updatedUrl.searchParams.set(
        'q',
        query
      );
    } else {
      updatedUrl.searchParams.delete(
        'q'
      );
    }

    window.history.replaceState(
      {},
      '',
      updatedUrl.toString()
    );
  }

  function initializeSearch(root) {
    if (
      !root ||
      root.getAttribute(
        'data-help-center-ready'
      ) === 'true'
    ) {
      return;
    }

    var form = root.querySelector(
      '[data-help-center-search-form]'
    );

    var input = root.querySelector(
      '[data-help-center-search-input]'
    );

    var clearButton = root.querySelector(
      '[data-help-center-search-clear]'
    );

    var submitButton = form
      ? form.querySelector(
          '.help-center-search__submit'
        )
      : null;

    var submitButtonDefaultText =
      submitButton
        ? submitButton.textContent.trim()
        : 'Search';

    var status = root.querySelector(
      '[data-help-center-search-status]'
    );

    var initialState =
      root.querySelector(
        '[data-help-center-search-initial]'
      );

    var resultsSection =
      root.querySelector(
        '[data-help-center-search-results-section]'
      );

    var resultsContainer =
      root.querySelector(
        '[data-help-center-search-results]'
      );

    var resultsCount =
      root.querySelector(
        '[data-help-center-search-count]'
      );

    var emptyState =
      root.querySelector(
        '[data-help-center-search-empty]'
      );

    var suggestionsSection =
      root.querySelector(
        '[data-help-center-search-suggestions]'
      );

    var suggestedTopicLinks =
      root.querySelectorAll(
        '[data-help-center-suggested-topic]'
      );

    var errorState =
      root.querySelector(
        '[data-help-center-search-error]'
      );

    var retryButton =
      root.querySelector(
        '[data-help-center-search-retry]'
      );

    if (
      !form ||
      !input ||
      !status ||
      !initialState ||
      !resultsSection ||
      !resultsContainer ||
      !emptyState ||
      !errorState
    ) {
      return;
    }

    root.setAttribute(
      'data-help-center-ready',
      'true'
    );

    var articles = null;
    var loadingPromise = null;
    var renderSequence = 0;

    function updateSearchClearButton() {
      if (!clearButton) {
        return;
      }

      clearButton.hidden =
        !String(
          input.value || ''
        ).trim();
    }

    function setSearchBusy(isBusy) {
      root.setAttribute(
        'aria-busy',
        isBusy ? 'true' : 'false'
      );

      if (!submitButton) {
        return;
      }

      submitButton.disabled = isBusy;
      submitButton.textContent =
        isBusy
          ? 'Searching…'
          : submitButtonDefaultText;
    }

    function showOnly(stateName) {
      initialState.hidden =
        stateName !== 'initial';

      resultsSection.hidden =
        stateName !== 'results';

      emptyState.hidden =
        stateName !== 'empty';

      errorState.hidden =
        stateName !== 'error';

      if (suggestionsSection) {
        suggestionsSection.hidden =
          stateName !== 'empty';
      }
    }

    function setLoadingStatus() {
      status.textContent =
        'Loading Help Center answers…';
    }

    function loadArticles(
      forceReload
    ) {
      if (forceReload) {
        clearCache(root);
        articles = null;
        loadingPromise = null;
      }

      if (articles) {
        return Promise.resolve(
          articles
        );
      }

      if (loadingPromise) {
        return loadingPromise;
      }

      setLoadingStatus();

      loadingPromise =
        fetchAllArticles(root)
          .then(
            function (
              loadedArticles
            ) {
              articles =
                loadedArticles;

              status.textContent =
                '';

              return articles;
            }
          )
          .catch(
            function (error) {
              loadingPromise =
                null;

              throw error;
            }
          );

      return loadingPromise;
    }

    function renderResults(query) {
      var cleanQuery =
        String(
          query || ''
        ).trim();

      renderSequence += 1;

      var currentRender =
        renderSequence;

      input.value = cleanQuery;

      updateSearchClearButton();
      updateSearchUrl(cleanQuery);

      resultsContainer
        .replaceChildren();

      if (!cleanQuery) {
        status.textContent = '';

        setSearchBusy(false);
        showOnly('initial');

        return;
      }

      showOnly('initial');
      setSearchBusy(true);
      setLoadingStatus();

      loadArticles(false)
        .then(
          function (
            loadedArticles
          ) {
            if (
              currentRender !==
              renderSequence
            ) {
              return;
            }

            setSearchBusy(false);

            var matches =
              searchArticles(
                loadedArticles,
                cleanQuery
              );

            resultsContainer
              .replaceChildren();

            if (!matches.length) {
              status.textContent =
                'No results found for “' +
                cleanQuery +
                '”.';

              showOnly('empty');

              pushDataLayerEvent({
                event:
                  'help_center_search',
                search_term:
                  cleanQuery,
                results_count: 0
              });

              return;
            }

            matches.forEach(
              function (article) {
                resultsContainer
                  .appendChild(
                    createResultAccordion(
                      root,
                      article
                    )
                  );
              }
            );

            if (resultsCount) {
              resultsCount.textContent =
                matches.length === 1
                  ? '1 answer found'
                  : matches.length +
                    ' answers found';
            }

            status.textContent =
              matches.length === 1
                ? '1 result found for “' +
                  cleanQuery +
                  '”.'
                : matches.length +
                  ' results found for “' +
                  cleanQuery +
                  '”.';

            showOnly('results');

            pushDataLayerEvent({
              event:
                'help_center_search',
              search_term:
                cleanQuery,
              results_count:
                matches.length
            });
          }
        )
        .catch(function (error) {
          console.error(
            '[Help Center Search]',
            error
          );

          if (
            currentRender !==
            renderSequence
          ) {
            return;
          }

          setSearchBusy(false);

          status.textContent =
            'Help Center search is temporarily unavailable.';

          showOnly('error');
        });
    }

    form.addEventListener(
      'submit',
      function (event) {
        event.preventDefault();

        renderResults(
          input.value
        );
      }
    );

    input.addEventListener(
      'input',
      updateSearchClearButton
    );

    input.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key === 'Escape' &&
          input.value
        ) {
          event.preventDefault();

          input.value = '';

          updateSearchClearButton();
          renderResults('');
          input.focus();
        }
      }
    );

    if (clearButton) {
      clearButton.addEventListener(
        'click',
        function () {
          input.value = '';

          updateSearchClearButton();
          renderResults('');
          input.focus();
        }
      );
    }

    root
      .querySelectorAll(
        '[data-help-center-popular-search]'
      )
      .forEach(
        function (link) {
          link.addEventListener(
            'click',
            function (event) {
              event.preventDefault();

              renderResults(
                link.getAttribute(
                  'data-help-center-popular-search'
                )
              );
            }
          );
        }
      );

    suggestedTopicLinks.forEach(
      function (link) {
        link.href =
          buildHelpCenterDestinationUrl(
            link.getAttribute(
              'href'
            )
          );

        link.addEventListener(
          'click',
          function () {
            pushDataLayerEvent({
              event:
                'help_center_suggested_topic_click',
              category_tag:
                link.getAttribute(
                  'data-help-center-suggested-topic'
                ) || '',
              destination_url:
                link.href
            });
          }
        );
      }
    );

    if (retryButton) {
      retryButton.addEventListener(
        'click',
        function () {
          loadArticles(true)
            .then(function () {
              renderResults(
                input.value
              );
            })
            .catch(function (error) {
              console.error(
                '[Help Center Search Retry]',
                error
              );

              showOnly('error');
            });
        }
      );
    }

    var initialUrl = new URL(
      window.location.href
    );

    var initialQuery =
      initialUrl.searchParams.get(
        'q'
      ) || '';

    if (initialQuery) {
      renderResults(
        initialQuery
      );
    } else {
      updateSearchClearButton();
      setSearchBusy(false);
      showOnly('initial');
    }
  }

  function initializeCategorySearch(
    root
  ) {
    if (
      !root ||
      root.getAttribute(
        'data-help-center-category-search-ready'
      ) === 'true'
    ) {
      return;
    }

    var searchArea =
      root.querySelector(
        '[data-help-center-category-search]'
      );

    var input =
      root.querySelector(
        '[data-help-center-category-search-input]'
      );

    var clearButton =
      root.querySelector(
        '[data-help-center-category-search-clear]'
      );

    var resetButton =
      root.querySelector(
        '[data-help-center-category-search-reset]'
      );

    var status =
      root.querySelector(
        '[data-help-center-category-search-status]'
      );

    var emptyState =
      root.querySelector(
        '[data-help-center-category-search-empty]'
      );

    var pagination =
      root.querySelector(
        '[data-help-center-category-pagination]'
      );

    var articleElements =
      Array.prototype.slice.call(
        root.querySelectorAll(
          '[data-help-center-category-article]'
        )
      );

    if (
      !searchArea ||
      !input ||
      !status ||
      !articleElements.length
    ) {
      return;
    }

    root.setAttribute(
      'data-help-center-category-search-ready',
      'true'
    );

    var categoryName =
      root.getAttribute(
        'data-help-center-category-name'
      ) || 'this category';

    var categoryTag =
      root.getAttribute(
        'data-help-center-category-tag'
      ) || '';

    articleElements.forEach(
      function (
        articleElement
      ) {
        var answer =
          findAccordionAnswerContainer(
            articleElement,
            [
              '.help-center-category-article__answer',
              '[data-help-center-category-answer]',
              '.help-center-category-article__body'
            ]
          );

        if (
          !answer ||
          answer.querySelector(
            '[data-help-center-answer-feedback]'
          )
        ) {
          return;
        }

        answer.appendChild(
          createAnswerFeedback({
            articleTitle:
              articleElement.getAttribute(
                'data-help-center-category-article-title'
              ) || '',
            articleHandle:
              articleElement.getAttribute(
                'data-help-center-category-article-handle'
              ) || '',
            articleUrl:
              articleElement.getAttribute(
                'data-help-center-category-article-url'
              ) || '',
            categoryName:
              categoryName,
            categoryTag:
              categoryTag,
            context:
              'category_accordion'
          })
        );
      }
    );

    function closeOtherArticles(
      activeArticle
    ) {
      articleElements.forEach(
        function (
          articleElement
        ) {
          if (
            articleElement !==
              activeArticle &&
            articleElement.open
          ) {
            articleElement.open =
              false;
          }
        }
      );
    }

    function setArticleInUrl(
      articleElement
    ) {
      if (
        !window.history ||
        !window.history.replaceState
      ) {
        return;
      }

      var url =
        new URL(
          window.location.href
        );

      if (
        articleElement &&
        articleElement.open
      ) {
        var articleHandle =
          articleElement.getAttribute(
            'data-help-center-category-article-handle'
          );

        if (articleHandle) {
          url.searchParams.set(
            'article',
            articleHandle
          );
        }
      } else {
        url.searchParams.delete(
          'article'
        );
      }

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    }

    articleElements.forEach(
      function (
        articleElement
      ) {
        articleElement.addEventListener(
          'toggle',
          function () {
            var articleHandle =
              articleElement.getAttribute(
                'data-help-center-category-article-handle'
              ) || '';

            var articleTitle =
              articleElement.getAttribute(
                'data-help-center-category-article-title'
              ) || '';

            if (articleElement.open) {
              closeOtherArticles(
                articleElement
              );

              setArticleInUrl(
                articleElement
              );

              pushDataLayerEvent({
                event:
                  'help_center_category_answer_open',
                article_title:
                  articleTitle,
                article_handle:
                  articleHandle,
                category_name:
                  categoryName
              });

              return;
            }

            var currentUrl =
              new URL(
                window.location.href
              );

            var currentArticleHandle =
              currentUrl.searchParams.get(
                'article'
              );

            if (
              currentArticleHandle &&
              currentArticleHandle ===
                articleHandle
            ) {
              setArticleInUrl(
                null
              );
            }
          }
        );
      }
    );

    function updateCategoryResults(
      query
    ) {
      var cleanQuery =
        String(
          query || ''
        ).trim();

      var normalizedQuery =
        normalizeText(
          cleanQuery
        );

      var terms =
        getSearchTerms(
          cleanQuery
        );

      var visibleCount = 0;

      articleElements.forEach(
        function (
          articleElement
        ) {
          var searchableText =
            normalizeText(
              articleElement.getAttribute(
                'data-help-center-category-search-text'
              ) || ''
            );

          var exactPhraseMatches =
            Boolean(
              normalizedQuery &&
              searchableText.indexOf(
                normalizedQuery
              ) !== -1
            );

          var allTermsMatch =
            terms.length > 0 &&
            terms.every(
              function (term) {
                return (
                  searchableText.indexOf(
                    term
                  ) !== -1
                );
              }
            );

          var isVisible =
            !normalizedQuery ||
            exactPhraseMatches ||
            allTermsMatch;

          articleElement.hidden =
            !isVisible;

          if (!isVisible) {
            articleElement.open =
              false;
          }

          if (isVisible) {
            visibleCount += 1;
          }
        }
      );

      if (clearButton) {
        clearButton.hidden =
          !cleanQuery;
      }

      if (emptyState) {
        emptyState.hidden =
          !cleanQuery ||
          visibleCount > 0;
      }

      if (pagination) {
        pagination.hidden =
          Boolean(
            cleanQuery
          );
      }

      if (!cleanQuery) {
        status.textContent = '';

        return;
      }

      if (visibleCount === 0) {
        status.textContent =
          'No answers found for “' +
          cleanQuery +
          '” in ' +
          categoryName +
          '.';

        return;
      }

      status.textContent =
        visibleCount === 1
          ? '1 answer found for “' +
            cleanQuery +
            '”.'
          : visibleCount +
            ' answers found for “' +
            cleanQuery +
            '”.';
    }

    function clearCategorySearch() {
      input.value = '';

      updateCategoryResults(
        ''
      );

      input.focus();
    }

    input.addEventListener(
      'input',
      function () {
        updateCategoryResults(
          input.value
        );
      }
    );

    input.addEventListener(
      'keydown',
      function (event) {
        if (
          event.key === 'Escape' &&
          input.value
        ) {
          event.preventDefault();

          clearCategorySearch();
        }
      }
    );

    if (clearButton) {
      clearButton.addEventListener(
        'click',
        clearCategorySearch
      );
    }

    if (resetButton) {
      resetButton.addEventListener(
        'click',
        clearCategorySearch
      );
    }

    updateCategoryResults(
      ''
    );

    var initialUrl =
      new URL(
        window.location.href
      );

    var requestedArticleHandle =
      initialUrl.searchParams.get(
        'article'
      );

    if (!requestedArticleHandle) {
      return;
    }

    var requestedArticle =
      articleElements.find(
        function (
          articleElement
        ) {
          return (
            articleElement.getAttribute(
              'data-help-center-category-article-handle'
            ) ===
            requestedArticleHandle
          );
        }
      );

    if (!requestedArticle) {
      return;
    }

    closeOtherArticles(
      requestedArticle
    );

    requestedArticle.open =
      true;

    window.requestAnimationFrame(
      function () {
        requestedArticle.scrollIntoView({
          behavior:
            window.matchMedia(
              '(prefers-reduced-motion: reduce)'
            ).matches
              ? 'auto'
              : 'smooth',
          block: 'center'
        });

        var requestedSummary =
          requestedArticle.querySelector(
            '.help-center-category-article__summary'
          );

        if (requestedSummary) {
          requestedSummary.focus({
            preventScroll: true
          });
        }
      }
    );
  }

  function initializeHomeFaqs(root) {
    if (
      !root ||
      root.getAttribute(
        'data-help-center-home-faq-ready'
      ) === 'true'
    ) {
      return;
    }

    var faqElements =
      Array.prototype.slice.call(
        root.querySelectorAll(
          '[data-help-center-home-faq]'
        )
      );

    if (!faqElements.length) {
      return;
    }

    root.setAttribute(
      'data-help-center-home-faq-ready',
      'true'
    );

    faqElements.forEach(
      function (
        faqElement
      ) {
        var answer =
          findAccordionAnswerContainer(
            faqElement,
            [
              '.help-center-home-article__answer',
              '[data-help-center-home-faq-answer]',
              '.help-center-home-article__body'
            ]
          );

        if (
          !answer ||
          answer.querySelector(
            '[data-help-center-answer-feedback]'
          )
        ) {
          return;
        }

        answer.appendChild(
          createAnswerFeedback({
            articleTitle:
              faqElement.getAttribute(
                'data-help-center-home-faq-title'
              ) || '',
            articleHandle:
              faqElement.getAttribute(
                'data-help-center-home-faq-handle'
              ) || '',
            articleUrl:
              faqElement.getAttribute(
                'data-help-center-home-faq-url'
              ) || '',
            categoryName:
              faqElement.getAttribute(
                'data-help-center-home-faq-category'
              ) || '',
            categoryTag:
              faqElement.getAttribute(
                'data-help-center-home-faq-category-tag'
              ) || '',
            context:
              'homepage_accordion'
          })
        );
      }
    );

    function closeOtherFaqs(
      activeFaq
    ) {
      faqElements.forEach(
        function (
          faqElement
        ) {
          if (
            faqElement !==
              activeFaq &&
            faqElement.open
          ) {
            faqElement.open =
              false;
          }
        }
      );
    }

    faqElements.forEach(
      function (
        faqElement
      ) {
        faqElement.addEventListener(
          'toggle',
          function () {
            if (!faqElement.open) {
              return;
            }

            closeOtherFaqs(
              faqElement
            );

            pushDataLayerEvent({
              event:
                'help_center_home_answer_open',
              article_title:
                faqElement.getAttribute(
                  'data-help-center-home-faq-title'
                ) || '',
              article_handle:
                faqElement.getAttribute(
                  'data-help-center-home-faq-handle'
                ) || ''
            });
          }
        );
      }
    );
  }

  function initializeHomePredictiveSearch(
  root
) {
  if (
    !root ||
    root.getAttribute(
      'data-help-center-home-predictive-ready'
    ) === 'true'
  ) {
    return;
  }

  var form =
    root.querySelector(
      '[data-help-center-home-search-form]'
    );

  var input =
    root.querySelector(
      '[data-help-center-home-search-input]'
    );

  var suggestions =
    root.querySelector(
      '[data-help-center-home-search-suggestions]'
    );

  var resultsContainer =
    root.querySelector(
      '[data-help-center-home-search-results]'
    );

  var allResultsLink =
    root.querySelector(
      '[data-help-center-home-search-all]'
    );

  if (
    !form ||
    !input ||
    !suggestions ||
    !resultsContainer ||
    !allResultsLink
  ) {
    return;
  }

  /*
   * The existing search engine expects data-index-url.
   * The homepage Liquid exposes
   * data-help-center-index-url, so copy it here.
   */
  var homepageIndexUrl =
    root.getAttribute(
      'data-help-center-index-url'
    );

  if (!homepageIndexUrl) {
    return;
  }

  root.setAttribute(
    'data-index-url',
    homepageIndexUrl
  );

  root.setAttribute(
    'data-help-center-home-predictive-ready',
    'true'
  );

  var articles = null;
  var loadingPromise = null;
  var debounceTimer = null;
  var requestSequence = 0;

  function closeSuggestions() {
    suggestions.hidden =
      true;

    input.setAttribute(
      'aria-expanded',
      'false'
    );
  }

  function openSuggestions() {
    suggestions.hidden =
      false;

    input.setAttribute(
      'aria-expanded',
      'true'
    );
  }

  function loadArticles() {
    if (articles) {
      return Promise.resolve(
        articles
      );
    }

    if (loadingPromise) {
      return loadingPromise;
    }

    loadingPromise =
      fetchAllArticles(
        root
      )
        .then(
          function (
            loadedArticles
          ) {
            articles =
              loadedArticles;

            return articles;
          }
        )
        .catch(
          function (
            error
          ) {
            loadingPromise =
              null;

            throw error;
          }
        );

    return loadingPromise;
  }

  function closeOtherResults(
    activeResult
  ) {
    resultsContainer
      .querySelectorAll(
        '.help-center-search-result[open]'
      )
      .forEach(
        function (
          result
        ) {
          if (
            result !==
            activeResult
          ) {
            result.open =
              false;
          }
        }
      );
  }

  function renderSuggestions(
    query
  ) {
    var cleanQuery =
      String(
        query || ''
      ).trim();

    requestSequence += 1;

    var currentRequest =
      requestSequence;

    resultsContainer
      .replaceChildren();

    if (
      cleanQuery.length < 2
    ) {
      allResultsLink.hidden =
        true;

      closeSuggestions();

      return;
    }

    loadArticles()
      .then(
        function (
          loadedArticles
        ) {
          if (
            currentRequest !==
            requestSequence
          ) {
            return;
          }

          var matches =
            searchArticles(
              loadedArticles,
              cleanQuery
            ).slice(
              0,
              5
            );

          resultsContainer
            .replaceChildren();

          matches.forEach(
            function (
              article
            ) {
              /*
               * Reuse the EXISTING Help Center
               * accordion component.
               */
              var accordion =
                createResultAccordion(
                  root,
                  article
                );

              /*
               * Predictive results remain inside
               * the dropdown instead of navigating.
               */
              accordion
                .removeAttribute(
                  'data-help-center-search-answer'
                );

              accordion.setAttribute(
                'data-help-center-predictive-answer',
                ''
              );

              accordion.addEventListener(
                'toggle',
                function () {
                  if (
                    accordion.open
                  ) {
                    closeOtherResults(
                      accordion
                    );
                  }
                }
              );

              resultsContainer
                .appendChild(
                  accordion
                );
            }
          );

          if (!matches.length) {
            var empty =
              document.createElement(
                'div'
              );

            empty.className =
              'help-center-home-search-suggestion--empty';

            empty.textContent =
              'No matching answers';

            resultsContainer
              .appendChild(
                empty
              );
          }

          allResultsLink.href =
            '/pages/help-center-search?q=' +
            encodeURIComponent(
              cleanQuery
            );

          allResultsLink.textContent =
            'View all results for “' +
            cleanQuery +
            '”';

          allResultsLink.hidden =
            false;

          openSuggestions();

          pushDataLayerEvent({
            event:
              'help_center_predictive_search',

            search_term:
              cleanQuery,

            results_count:
              matches.length
          });
        }
      )
      .catch(
        function (
          error
        ) {
          console.error(
            '[Help Center Predictive Search]',
            error
          );

          closeSuggestions();
        }
      );
  }

  input.addEventListener(
    'input',
    function () {
      clearTimeout(
        debounceTimer
      );

      debounceTimer =
        setTimeout(
          function () {
            renderSuggestions(
              input.value
            );
          },
          200
        );
    }
  );

  input.addEventListener(
    'focus',
    function () {
      var cleanQuery =
        String(
          input.value || ''
        ).trim();

      if (
        cleanQuery.length >= 2 &&
        resultsContainer
          .children
          .length
      ) {
        openSuggestions();
      }
    }
  );

  input.addEventListener(
    'keydown',
    function (
      event
    ) {
      if (
        event.key ===
        'Escape'
      ) {
        event.preventDefault();

        closeSuggestions();
      }
    }
  );

  form.addEventListener(
    'submit',
    function () {
      closeSuggestions();
    }
  );

  allResultsLink
    .addEventListener(
      'click',
      function () {
        pushDataLayerEvent({
          event:
            'help_center_predictive_view_all',

          search_term:
            String(
              input.value || ''
            ).trim()
        });
      }
    );

  document.addEventListener(
    'pointerdown',
    function (
      event
    ) {
      if (
        !form.contains(
          event.target
        )
      ) {
        closeSuggestions();
      }
    }
  );
}


function initializeAllHomePredictiveSearch(
  container
) {
  var scope =
    container ||
    document;

  if (
    scope.matches &&
    scope.matches(
      '[data-help-center-home-predictive-search]'
    )
  ) {
    initializeHomePredictiveSearch(
      scope
    );
  }

  scope
    .querySelectorAll(
      '[data-help-center-home-predictive-search]'
    )
    .forEach(
      initializeHomePredictiveSearch
    );
}
  function initializeAllSearchPages(
    container
  ) {
    var scope =
      container || document;

    if (
      scope.matches &&
      scope.matches(
        '[data-help-center-search]'
      )
    ) {
      initializeSearch(scope);
    }

    scope
      .querySelectorAll(
        '[data-help-center-search]'
      )
      .forEach(
        initializeSearch
      );
  }

  function initializeAllCategorySearchPages(
    container
  ) {
    var scope =
      container || document;

    if (
      scope.matches &&
      scope.matches(
        '[data-help-center-category-page]'
      )
    ) {
      initializeCategorySearch(
        scope
      );
    }

    scope
      .querySelectorAll(
        '[data-help-center-category-page]'
      )
      .forEach(
        initializeCategorySearch
      );
  }

  function initializeAllHomeFaqPages(
    container
  ) {
    var scope =
      container || document;

    if (
      scope.matches &&
      scope.matches(
        '[data-help-center-template="home"]'
      )
    ) {
      initializeHomeFaqs(
        scope
      );
    }

    scope
      .querySelectorAll(
        '[data-help-center-template="home"]'
      )
      .forEach(
        initializeHomeFaqs
      );
  }

  function initializeAll(
  container
) {
  initializeAllSearchPages(
    container
  );

  initializeAllCategorySearchPages(
    container
  );

  initializeAllHomeFaqPages(
    container
  );

  initializeAllHomePredictiveSearch(
    container
  );
}

  document.addEventListener(
    'DOMContentLoaded',
    function () {
      initializeAll(
        document
      );
    }
  );

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initializeAll(
        event.target
      );
    }
  );
})();

